import {
  faCreditCard,
  faFileInvoiceDollar,
  faHospital,
  faUserDoctor,
} from '@fortawesome/pro-solid-svg-icons';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';

/**
 * Common layout constants for analytics tables
 */
export const ANALYTICS_TABLE_LAYOUT = {
  /** Width of the sticky Geography column */
  GEOGRAPHY_COLUMN_WIDTH: 272,
  /** Left position where the separator line appears */
  SEPARATOR_LEFT_POSITION: 271,
  /** Height of table header */
  HEADER_HEIGHT: 48, // 3rem = 48px
  /** Height calculation for empty states */
  EMPTY_STATE_HEIGHT: 'calc(100vh - 18.75rem)', // 18.75rem = 300px
} as const;

/**
 * Container width (px) below which each header control stops fitting inline and
 * drops into the "More options" overflow menu. Widest control collapses first.
 *
 * The Options control has no breakpoint of its own — it collapses as soon as the
 * menu exists at all, so its slicing controls sit flat in the menu rather than
 * behind a dropdown inside a dropdown. See `getHeaderOverflowLayout`.
 */
export const HEADER_CONTROL_BREAKPOINTS = {
  time: 850,
  export: 750,
  selector: 600,
} as const;

export type HeaderBreakpoint =
  (typeof HEADER_CONTROL_BREAKPOINTS)[keyof typeof HEADER_CONTROL_BREAKPOINTS];

/**
 * Tailwind can only see class names it finds as literals in the source, so the
 * container-query variants are spelled out here rather than interpolated.
 */
export const HEADER_INLINE_CLASS: Record<HeaderBreakpoint, string> = {
  850: 'hidden @[850px]/header:block',
  750: 'hidden @[750px]/header:block',
  600: 'hidden @[600px]/header:block',
};

export const HEADER_OVERFLOW_ITEM_CLASS: Record<HeaderBreakpoint, string> = {
  850: '@[850px]/header:hidden px-4 py-3',
  750: '@[750px]/header:hidden px-4 py-3',
  600: '@[600px]/header:hidden px-4 py-3',
};

export const HEADER_OVERFLOW_TRIGGER_CLASS: Record<HeaderBreakpoint, string> = {
  850: 'block @[850px]/header:hidden',
  750: 'block @[750px]/header:hidden',
  600: 'block @[600px]/header:hidden',
};

/**
 * Referring (`sending`) volume is not reported by site, so the Sites volume type
 * and Referring mode are mutually exclusive. Whichever one is already selected
 * disables the other, so the invalid combination can't be reached from either
 * direction.
 */
export const REFERRING_DISABLED_FOR_SITES_TOOLTIP =
  'Referring is not available for Sites';

export const SITES_DISABLED_FOR_REFERRING_TOOLTIP =
  'Sites is not available in Referring mode';

/**
 * The only insights that read payer names, so the Payer Name filter is offered
 * (and counted, and sent) on these tables alone rather than on every insight.
 */
export const PAYER_NAME_FILTER_LOCATIONS: string[] = [
  ANALYTICS_LOCATION.PAYER_MIX,
  ANALYTICS_LOCATION.REIMBURSEMENT,
];

/** Reports that count by provider or by site rather than by claim volume. */
export const PROVIDER_SITES_OPTIONS = [
  { value: 'providers' as const, label: 'Providers', icon: faUserDoctor },
  { value: 'sites' as const, label: 'Sites', icon: faHospital },
];

/**
 * Payer Mix / Average Reimbursement can break down by the fixed payer *type*
 * enum or by specific payer *names*. Type is the default.
 */
export const PAYER_GROUP_BY_OPTIONS = [
  { value: 'payer_type' as const, label: 'Payer Type', icon: faCreditCard },
  {
    value: 'payer_name' as const,
    label: 'Payer Name',
    icon: faFileInvoiceDollar,
  },
];

/**
 * Catch-all column the backend returns in payer-name mode for every payer
 * outside the broken-out set. Must match `OTHER_PAYER_LABEL` in
 * `data_analytics/services/territory_insights/payer_grouping.py`.
 */
export const OTHER_PAYERS_LABEL = 'Other Payers';

export const OTHER_PAYERS_TOOLTIP =
  'Every payer without its own column, combined. Use the Payer Name filter to choose which payers are broken out.';

/** Payer-type-mode catch-all column, which has a different meaning than the above. */
export const OTHER_PAYER_TYPE_LABEL = 'Other';

export const OTHER_PAYER_TYPE_TOOLTIP =
  'Payer type unknown due to incomplete data, unmatchable payer identifier, or special systems (VA, clinical trials, etc.).';
